<template>
  <div>
    <BaseElForm
      ref="formRef"
      :model="curCartItem"
      :rules="formRules"
      label-position="top"
      class="form-wrapper"
    >
      <template v-if="!loading.coreFlow">
        <!-- CBGISSUE-243：一次有兩個以上欄位失效時，最上方補一條摘要 -->
        <p v-if="staleSummary" class="mb-2 text-sm text-gray-80">{{ staleSummary }}</p>
        <BaseElFormItem :label="displayData.formItemLabel.serviceType">
          <BaseElSelect
            v-model="curCartItem.serviceType"
            testName="formData_type"
            value-key="id"
            :placeholder="displayData.select.placeholder"
            :no-data-text="displayData.noDataText"
            @change="onServiceTypeChange"
          >
            <BaseElSelectOption
              v-for="item in serviceTypeConfigList"
              :key="item.value"
              :label="$t(item.label)"
              :value="item.value"
            />
          </BaseElSelect>
        </BaseElFormItem>
        <BaseElFormItem v-if="showControl.formItem.serviceUnit" prop="serviceUnit">
          <template slot="label">
            <FormItemTooltipLabel :label="displayData.formItemLabel.serviceUnit" :tooltipWidth="200" style="display: flex !important">
              <div v-html="displayData.formItemTooltip.serviceUnit" />
            </FormItemTooltipLabel>
          </template>
          <div>
            <BaseElSelect
              v-model="curCartItem.serviceUnit"
              testName="formData_serviceUnit"
              value-key="id"
              @change="onServiceUnitChange"
              @clear="onServiceUnitChange"
            >
              <BaseElSelectOption
                v-for="unit in displayServiceUnitOptions"
                :key="unit.id"
                :value="unit"
                :label="staleOptionLabel(unit.name, optionStaleReason(unit))"
                :disabled="Boolean(optionStaleReason(unit))"
              />
            </BaseElSelect>
            <p
              v-show="serviceUnitSelectHint.content"
              :style="`color: ${serviceUnitSelectHint.color}`"
            >
              {{ serviceUnitSelectHint.content }}
            </p>
            <p v-if="staleFieldHints.serviceUnit" class="text-sm text-gray-80">{{ staleFieldHints.serviceUnit }}</p>
          </div>
        </BaseElFormItem>
        <BaseElFormItem v-if="showControl.formItem.category" :label="displayData.formItemLabel.category" prop="category">
          <BaseElSelect
            v-model="curCartItem.category"
            testName="formData_category"
            value-key="id"
            @change="onCategoryChange"
          >
            <BaseElSelectOption
              v-for="category in displayCategoryOptions"
              :key="category.id"
              :value="category"
              :label="category.name"
            />
          </BaseElSelect>
        </BaseElFormItem>
        <BaseElFormItem v-if="showControl.formItem.service" :label="displayData.formItemLabel.service" prop="service">
          <BaseElSelect
            v-model="curCartItem.service"
            filterable
            testName="formData_service"
            value-key="id"
            :no-data-text="staleEmptyText.service"
            @change="onServiceChange"
            @clear="onServiceChange"
          >
            <BaseElSelectOption
              v-for="service in displayServiceOptions"
              :key="service.id"
              :value="service"
              :label="staleOptionLabel(service.name, optionStaleReason(service))"
              :disabled="Boolean(optionStaleReason(service))"
            />
          </BaseElSelect>
          <p v-if="staleFieldHints.service" class="text-sm text-gray-80">{{ staleFieldHints.service }}</p>
        </BaseElFormItem>
        <BaseElFormItem v-if="showControl.formItem.subService" :label="displayData.formItemLabel.subService" prop="subService">
          <BaseElSelect
            v-model="curCartItem.subService"
            testName="formData_subService"
            value-key="id"
            :no-data-text="staleEmptyText.subService"
            @change="onSubServiceChange"
          >
            <BaseElSelectOption
              v-for="service in displaySubServiceOptions"
              :key="service.id"
              :label="staleOptionLabel(service.name, optionStaleReason(service))"
              :disabled="Boolean(optionStaleReason(service))"
              :value="service"
            />
          </BaseElSelect>
          <p v-if="staleFieldHints.subService" class="text-sm text-gray-80">{{ staleFieldHints.subService }}</p>
        </BaseElFormItem>
        <BaseElFormItem v-if="showControl.formItem.resources" :label="displayData.formItemLabel.resources" prop="resources">
          <BaseElSelect
            v-model="curCartItem.resources[0]"
            testName="formData_service"
            :disabled="disabledControl.formItem.resources"
            :placeholder="displayText.placeholder.formItem.resources"
            value-key="id"
            collapse-tags
            :multiple-limit="1"
            :no-data-text="staleEmptyText.resources"
            @change="onResourceChange"
          >
            <BaseElSelectOption
              v-for="service in displayResourceOptions"
              :key="service.id"
              :value="service"
              :label="staleOptionLabel(service.name, optionStaleReason(service))"
              :disabled="Boolean(optionStaleReason(service))"
            />
          </BaseElSelect>
          <p v-if="staleFieldHints.resources" class="text-sm text-gray-80">{{ staleFieldHints.resources }}</p>
        </BaseElFormItem>
        <BaseElFormItem v-if="showControl.formItem.attachedServices" :label="displayData.formItemLabel.attachedServices" prop="attachedServices">
          <BaseElSelect
            v-model="curCartItem.attachedServices"
            multiple
            collapse-tags
            value-key="id"
            @change="onAttachedServicesChange"
          >
            <BaseElSelectOption
              v-for="service in displayAttachServiceOptions"
              :key="service.id"
              :value="service"
              :label="staleOptionLabel(service.name, optionStaleReason(service))"
              :disabled="Boolean(optionStaleReason(service))"
            />
          </BaseElSelect>
        </BaseElFormItem>
        <BaseElFormItem v-if="showControl.formItem.bookingTime" :label="displayData.formItemLabel.bookingTime">
          <BaseElSelect
            v-model="curCartItem.bookingTime"
            testName="bookingTime"
            :placeholder="displayData.select.placeholder"
            @change="onBookingTimeChange"
          >
            <BaseElSelectOption
              v-for="(item, index) in displayBookingTimeOptions"
              :key="index"
              :label="timeOptionLabel(item, 'bookingTime')"
              :value="item"
            />
          </BaseElSelect>
          <p v-if="staleFieldHints.bookingTime" class="text-sm text-gray-80">{{ staleFieldHints.bookingTime }}</p>
        </BaseElFormItem>
        <BaseElFormItem v-if="showControl.formItem.resourceBookingTime">
          <template #label>
            <FormItemTooltipLabel :label="displayData.formItemLabel.resourceBookingTime">
              <div>
                {{ displayData.formItemTooltip.resourceBookingTime }}
              </div>
            </FormItemTooltipLabel>
          </template>
          <BaseElSelect
            v-model="curCartItem.resourceBookingTime"
            testName="formData_resourceTotalTime"
            :placeholder="displayData.select.placeholder"
            @change="onResourceBookingTimeChange"
          >
            <BaseElSelectOption
              v-for="(item, index) in displayResourceBookingTimeOptions"
              :key="index"
              :label="timeOptionLabel(item, 'resourceBookingTime')"
              :value="item"
            />
          </BaseElSelect>
          <p v-if="staleFieldHints.resourceBookingTime" class="text-sm text-gray-80">{{ staleFieldHints.resourceBookingTime }}</p>
        </BaseElFormItem>
        <BaseElFormItem v-if="showControl.formItem.isOverTIme" :label="displayData.formItemLabel.isOverTime">
          <BaseElRadioGroup v-model="curCartItem.isOverTime" @change="onIsOverTimeChange">
            <BaseElRadio :label="true">{{ displayData.isOverTime.yes }}</BaseElRadio>
            <BaseElRadio :label="false">{{ displayData.isOverTime.no }}</BaseElRadio>
          </BaseElRadioGroup>
        </BaseElFormItem>
        <BaseElFormItem v-if="showControl.formItem.date" :label="displayData.formItemLabel.date" prop="date">
          <el-date-picker
            ref="datePickerRef"
            v-model="curCartItem.date"
            editable
            :picker-options="datePickerOptions"
            type="date"
            :placeholder="displayData.date.placeholder"
            @focus="getDateOptions"
            @change="onDateChange"
          />
          <!-- CBGISSUE-243：綁定已變更導致算不出可預約時段時，說明原因（前端可判斷的情況） -->
          <p v-if="staleFieldHints.dateTime" class="text-sm text-gray-80">{{ staleFieldHints.dateTime }}</p>
        </BaseElFormItem>
        <BaseElFormItem
          v-if="showControl.formItem.time.timeSelect || showControl.formItem.time.overTimeSelect"
          :label="displayData.formItemLabel.time"
          prop="time"
        >
          <!-- <div v-if="!formData.date" class="text-gray-100">請先選擇預約日期</div> -->
          <BaseElSelect
            v-if="showControl.formItem.time.timeSelect"
            v-model="curCartItem.time"
            testName="formData_time"
            :placeholder="displayData.select.placeholder"
            :no-data-text="displayData.select.empty"
            @change="onTimeChange"
          >
            <BaseElSelectOption
              v-for="item in displayAvailableTimes"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </BaseElSelect>

          <!-- <BaseElSelect
            v-if="showControl.formItem.time.overTimeSelect"
            v-model="curCartItem.time"
            testName="formData_time"
            :placeholder="displayData.select.placeholder"
            :no-data-text="displayData.select.empty"
            allow-create
            filterable
            @change="onTimeChange"
          >
            <BaseElSelectOption
              v-for="item in displayOvertimeTimeOptions"
              :key="item.label"
              :label="item.label"
              :value="item.value"
            />
          </BaseElSelect> -->

          <BaseTimePicker
            v-if="showControl.formItem.time.overTimeSelect"
            :date="curCartItem.date"
            :value.sync="curCartItem.time"
            :picker-options="{
              selectableRange: '00:00:00 - 23:59:59'
            }"
            :placeholder="displayData.select.placeholder"
            :clearable="false"
            @change="onTimeChange"
          />
        </BaseElFormItem>
        <BaseElFormItem v-if="showControl.formItem.classTickets" prop="classTickets">
          <template #label>
            <div class="flex flex-col" style="gap: 4px">
              <FormItemTooltipLabel :label="displayData.formItemLabel.classTickets">
                <p>{{ displayData.formItemTooltip.classTicket }}</p>
              </FormItemTooltipLabel>
              <p v-if="showControl.formItemHint.classTicket" class="text-sm text-gray-60">{{ displayData.formItemHint.classTicket }}</p>
            </div>
          </template>
          <BaseElSelect
            v-model="curCartItem.classTickets"
            testName="formData_service_classTicket"
            value-key="id"
            multiple
            :multiple-limit="1"
            @change="onClassTicketChange"
          >
            <BaseElSelectOption
              v-for="classTicket in displayClassTicketOptions"
              :key="classTicket.id"
              :label="classTicket.label"
              :disabled="classTicket.disabled"
              :value="classTicket"
            />
          </BaseElSelect>
        </BaseElFormItem>
      </template>
    </BaseElForm>
  </div>
</template>

<script>
import axios from 'axios'
import { notSpecifyRulesConfig, serviceTypeConfig } from '@/config/reservation'
import { usePermissions } from '@/use/permissions'
import { appointmentModeType, featureKeys, useCreateAppointment } from '@/use/useCreateAppointment'
import { omit, map, get, includes, cloneDeep, filter, find, uniq, intersection, forEach, reduce, findIndex, isEmpty, isNull, compact } from 'lodash'
import { computed, defineComponent, reactive, ref, nextTick, onMounted, onBeforeUnmount, watch } from 'vue'
import FormItemTooltipLabel from '@/components/Form/FormItemTooltipLabel.vue'
import { FindService } from '@/api/service'
import { useShop } from '@/use/shop'
import { generateNumbers, sortOrder } from '@/utils/helper'
import { generateFormatPeriod } from '@/utils/time'
import { formatDate, getMonth, thisMonth } from '@/utils/date'

import { FindServiceUnit } from '@/api/serviceUnit'
import { apiFormatAdaptor } from '@/utils/api'
import dayjs from '@/lib/dayjs'
import { CheckReservation } from '@/api/reservation'
import { useVModel } from '@vueuse/core'
import { noEmptyRules } from '@/validation'
import store from '@/store'
import { i18n } from '@/plugins/i18n/i18n'
import BaseTimePicker from '@/components/BaseTimePicker.vue'
import { availabilityEndpoints, useAvailabilityQuery } from '@/use/useAvailabilityQuery'
import {
  markStaleOption,
  staleBlockedMessage,
  optionStaleReason,
  staleEmptyHint,
  staleFieldHint,
  staleOptionLabel,
  staleReasonConfig,
  staleSummaryText,
  virtualUnitIds,
  withPreservedOption,
} from '@/use/useReservationStaleField'
import { createAvailabilityPickerPanelBinding } from '@/use/useAvailabilityPickerPanel'

export default defineComponent({
  name: 'AppointmentCoreFlowBlock',
  components: { FormItemTooltipLabel, BaseTimePicker },
  props: {
    hideDateTimeSelector: {
      type: Boolean,
      default: false,
    },
    hideClassTicketSelector: {
      type: Boolean,
      default: false,
    },
    cartItemId: {
      type: String,
    },
    cartItem: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props, { emit }) {
    const { shopId } = useShop()
    const { checkAction } = usePermissions()
    const {
      cart,
      context, resourceData, configData, memberData,
      getCartItemPreReservationsPayload,
      updateCartItemData,
      defaultSelectClassTicket,
      calculateCheckoutCashbackLimit,
      beginFlow,
      endFlow,
      pruneAfterCartItem,
      clearAllFormFieldsValidate,
      presetData,
    } = useCreateAppointment()
    // 花鳥川：建立預約是否支援選預約類別
    const useKachogawaCategory = computed(() => checkAction(featureKeys.useKachogawaCategory))
    const datePickerRef = ref(null)
    const dataPlaceholder = reactive({
      services: [],
      attachedServices: [],
      availableDate: [],
      availableTimes: [],
      bookingTimes: [],
    })
    const availabilityPayload = ref(null)
    const { query: availabilityQuery, cancel: cancelAvailability, getCachedData } = useAvailabilityQuery({
      endpoint: availabilityEndpoints.times,
      payload: availabilityPayload,
    })
    const availabilityPickerPanelBinding = createAvailabilityPickerPanelBinding()
    const shouldApplyPresetPeriod = ref(false)
    const loading = reactive({
      coreFlow: false,
    })

    const curCartItem = useVModel(props, 'cartItem', emit)
    const formRef = ref(null)
    const formRules = computed(() => {
      const rules = {
        service: [noEmptyRules()],
        serviceUnit: [noEmptyRules()],
        date: [noEmptyRules()],
        time: [noEmptyRules()],
      }

      const selectedService = get(curCartItem.value, 'service')

      // 如果服務有子項目
      if (get(selectedService, 'enableSubService')) {
        rules.subService = [noEmptyRules()]
      }

      // 如果有啟用預約堂票
      if (get(configData.reservation, 'enableClassTicket')) {
        // 如果服務必選堂票
        if (get(selectedService, 'enableClassTicket') && get(selectedService, 'requiredClassTicket')) {
          rules.classTickets = [{ type: 'array', required: true, trigger: ['change', 'blur'], message: '請選擇堂票' }]
        }
      }

      if (curCartItem.value.isResourceService()) {
        rules.resources = [{ type: 'array', required: true, trigger: ['change', 'blur'], message: '請選擇服務設備' }]
      }

      // 花鳥川：預約類別必填
      if (useKachogawaCategory.value) {
        rules.category = [noEmptyRules()]
      }

      // CBGISSUE-243：欄位上保留的失效歷史值不可直接送出，停在該欄位顯示同一句原因。
      // 只有真的失效時才掛上規則，正常流程的 rules 與原本完全相同。
      forEach(['serviceUnit', 'service', 'subService', 'resources'], (field) => {
        const hint = get(staleFieldHints.value, field)
        if (!hint || !rules[field]) return
        rules[field] = [
          ...rules[field],
          {
            trigger: ['change', 'blur'],
            validator: (rule, value, callback) => callback(new Error(staleBlockedMessage())),
          },
        ]
      })

      return rules
    })
    const role = computed(() => get(store.getters, 'role'))

    const isShopOperation = computed(() => ['customRole:shopOperationDisableSchedule', 'shopOperation'].includes(role.value))
    // ?? 畫面顯示用的資料
    const displayData = computed(() => {
      const data = {
        formItemLabel: {
          serviceType: i18n.t('createReservationService.type.title'),
          category: i18n.t('orderDetailBlock.appointmentCategory.title'),
          serviceUnit: i18n.t('orderDetailBlock.serviceUnit.title'),
          service: i18n.t('orderDetailBlock.serviceName.title'),
          subService: i18n.t('orderDetailBlock.subServiceName.title'),
          resources: i18n.t('orderDetailBlock.resource.title'),
          attachedServices: i18n.t('orderDetailBlock.attachService.title'),
          bookingTime: i18n.t('editReservationDialog.totalTime.title'),
          resourceBookingTime: i18n.t('editReservationDialog.resourceTotalTime.title'),
          isOverTime: i18n.t('editReservationDialog.isOverTime.title'),
          classTickets: i18n.t('editReservationDialog.classTicket.title'),
          date: i18n.t('editReservationDialog.date.title'),
          time: i18n.t('editReservationDialog.period.title'),
        },
        formItemTooltip: {
          classTicket: i18n.t('editReservationDialog.classTicket.tooltip'),
          serviceUnit: i18n.t('orderDetailBlock.serviceUnit.tooltip'),
          resourceBookingTime: i18n.t('editReservationDialog.resourceTotalTime.tooltip'),
        },
        formItemHint: {
          classTicket: i18n.t('editReservationDialog.classTicket.hint'),
        },
        select: {
          placholder: i18n.t('common.select.placeholder'),
          empty: i18n.t('common.select.empty.text'),
        },
        noDataText: i18n.t('common.table.empty.text'),
        date: {
          placeholder: i18n.t('editReservationDialog.date.placeholder'),
        },
        isOverTime: {
          yes: i18n.t('common.yes.text'),
          no: i18n.t('common.no.text'),
        },
      }

      const serviceType = curCartItem.value.serviceType
      if (serviceType === serviceTypeConfig.resourceService.value) {
        data.formItemTooltip.resourceBookingTime = i18n.t('appointmentCreate.formItemHint.pureResourceBookingTime.content')
      }

      return data
    })

    // ?? 元件顯示控制
    const showControl = computed(() => {
      const controls = {
        formItem: {
          category: useKachogawaCategory.value,
          serviceUnit: true,
          service: true,
          subService: false,
          attachedServices: false,
          resources: false,
          bookingTime: false,
          resourceBookingTime: false,
          date: false,
          isOverTIme: true,
          time: {
            timeSelect: false,
            overTimeSelect: false,
          },
          classTickets: false,
        },
        formItemHint: {
          classTicket: false,
        },
      }

      if (context.peopleCount > 1) {
        controls.formItemHint.classTicket = true
      }

      const serviceType = curCartItem.value.serviceType
      if (serviceType === serviceTypeConfig.resourceService.value) {
        controls.formItem.serviceUnit = false
        controls.formItem.resources = true
      } else if (serviceType === serviceTypeConfig.humanAndResourceService.value) {
        controls.formItem.serviceUnit = true
        controls.formItem.resources = true
      }

      const selectedService = curCartItem.value.service
      if (selectedService) {
        controls.formItem.attachedServices = true
        if (get(selectedService, 'enableSubService')) {
          controls.formItem.subService = true
        }

        if (curCartItem.value.isHumanResourceService()) {
          controls.formItem.bookingTime = true
          controls.formItem.resourceBookingTime = true
        } else if (curCartItem.value.isResourceService()) {
          controls.formItem.bookingTime = false
          controls.formItem.resourceBookingTime = true
        } else {
          controls.formItem.bookingTime = true
          controls.formItem.resourceBookingTime = false
        }

        controls.formItem.date = true
      }

      if (curCartItem.value.date) {
        const isOverTime = curCartItem.value.isOverTime
        if (isOverTime) {
          controls.formItem.time.timeSelect = false
          controls.formItem.time.overTimeSelect = true
        } else {
          controls.formItem.time.timeSelect = true
          controls.formItem.time.overTimeSelect = false
        }
      }

      if (get(configData, 'reservation.enableClassTicket')) {
        if (props.hideClassTicketSelector) {
          controls.formItem.classTickets = false
        } else {
          if (curCartItem.value.time) {
            controls.formItem.classTickets = true
          }
        }
      }

      if (props.hideDateTimeSelector) {
        controls.formItem.date = false
        controls.formItem.time.timeSelect = false
        controls.formItem.time.overTimeSelect = false
        controls.formItem.isOverTIme = false
      }

      return controls
    })

    // ?? 禁用控制
    const disabledControl = computed(() => {
      const controls = {
        formItem: {
          resources: false,
        },
      }

      if (isEmpty(displayResourceOptions.value) && curCartItem.value.service) {
        controls.formItem.resources = true
      }

      return controls
    })

    const displayText = computed(() => {
      const text = {
        placeholder: {
          formItem: {
            resources: i18n.t('common.select.placeholder'),
          },
        },
      }

      if (isEmpty(displayResourceOptions.value) && curCartItem.value.service) {
        text.placeholder.formItem.resources = i18n.t('common.select.empty.text')
      }

      return text
    })

    // ?? 顯示的服務類型選項
    const serviceTypeConfigList = computed(() => {
      const omitList = []
      if (!isShopOperation.value) {
        if (!checkAction('admin.resourceItem.page')) {
          omitList.push('resourceService', 'humanAndResourceService')
        }
      }
      return omit(serviceTypeConfig, omitList)
    })

    // ?? 顯示的服務人員選項
    const serviceUnitIOptions = computed(() => {
      const serviceType = curCartItem.value.serviceType
      if (serviceType === serviceTypeConfig.resourceService.value) return []

      const base = cloneDeep(resourceData.serviceUnits)

      const useNotSpecify = get(configData.reservation, 'useNotSpecify')
      if (isShopOperation.value) return base
      if (useNotSpecify) {
        base.unshift({
          id: 'notSpecify',
          name: i18n.t('orderDetailBlock.serviceUnit.notSpecify.title'),
        })
      }

      base.unshift({
        id: 'all',
        name: i18n.t('orderDetailBlock.serviceUnit.all.title'),
      })
      return base
    })

    // ?? 顯示的服務人員選項（補回已被刪除但仍被選取的歷史值）
    const displayServiceUnitOptions = computed(() => {
      return withPreservedOption(serviceUnitIOptions.value, curCartItem.value.serviceUnit, staleFields.value.serviceUnit)
    })

    // ?? 顯示的服務人員選項的提示
    const serviceUnitSelectHint = computed(() => {
      const useNotSpecify = get(configData.reservation, 'useNotSpecify')
      const notSpecifyRule = get(configData.reservation, 'notSpecifyRule')
      const selectUnitId = get(curCartItem.value.serviceUnit, 'id')
      if (!includes(['notSpecify', 'all'], selectUnitId)) return { content: '', color: 'var(--base)' }

      if (useNotSpecify) {
        const rule = get(notSpecifyRulesConfig, `${notSpecifyRule}.label`)
        return { content: i18n.t('orderDetailBlock.useNotSpecify.title', { rule: i18n.t(rule) }), color: 'var(--base)' }
      } else if (!useNotSpecify) {
        if (selectUnitId === 'all') return { content: i18n.t('orderDetailBlock.serviceUnit.all.hint'), color: 'var(--base)' }
        else if (selectUnitId === 'notSpecify') return { content: i18n.t('orderDetailBlock.serviceunit.notSpecify.hint'), color: 'var(--danger)' }
      }
      return { content: '', color: 'var(--base)' }
    })

    // ?? 顯示的服務項目選項
    // => 服務項目是否符合目前的服務性質
    const matchServiceType = (service, serviceType) => {
      if (!service) return false
      if (serviceType === serviceTypeConfig.resourceService.value) {
        return !service.enableAppointmentUnit && service.enableResourceItem
      }
      if (serviceType === serviceTypeConfig.humanAndResourceService.value) {
        return Boolean(service.enableAppointmentUnit && service.enableResourceItem)
      }
      return Boolean(service.enableAppointmentUnit && !service.enableResourceItem)
    }

    // ?? 服務項目 -> 所屬預約類別 id 的對照表
    // CBGISSUE-243：dataPlaceholder.services 可能來自服務人員詳情（appointmentUnit findOne），
    // 該 payload 的 AppointmentServices 沒有帶 AppointmentCategories，直接拿選項自身的
    // AppointmentCategories 過濾會把所有服務項目濾掉（畫面顯示「無資料」）。
    // 類別歸屬統一以全店服務項目清單（GetService，帶 AppointmentCategories）為準。
    const serviceCategoryIdsMap = computed(() => {
      return reduce(resourceData.services, (acc, service) => {
        acc[service.id] = map(get(service, 'AppointmentCategories'), 'id')
        return acc
      }, {})
    })

    // ?? 依服務性質 / 服務人員綁定 / 預約類別篩出的服務項目（不含失效歷史值）
    const baseServiceOptions = computed(() => {
      const serviceType = curCartItem.value.serviceType
      let serviceOptionList
      if (curCartItem.value.isResourceService()) {
        if (serviceType === serviceTypeConfig.resourceService.value) {
          serviceOptionList = filter(dataPlaceholder.services, (i) => !i.enableAppointmentUnit && i.enableResourceItem)
        } else if (serviceType === serviceTypeConfig.humanAndResourceService.value) {
          serviceOptionList = filter(dataPlaceholder.services, (i) => i.enableAppointmentUnit && i.enableResourceItem)
        }
      }
      if (!serviceOptionList) {
        serviceOptionList = filter(dataPlaceholder.services, (i) => i.enableAppointmentUnit && !i.enableResourceItem)
      }
      // 花鳥川：依所選預約類別再篩出該類別底下的服務
      const categoryId = get(curCartItem.value, 'category.id')
      if (useKachogawaCategory.value && categoryId) {
        serviceOptionList = filter(serviceOptionList, (i) => includes(get(serviceCategoryIdsMap.value, i.id, []), categoryId))
      }
      return sortOrder(serviceOptionList)
    })

    // ?? 顯示的服務項目選項（補回已失效但仍被選取的歷史值）
    const displayServiceOptions = computed(() => {
      return withPreservedOption(baseServiceOptions.value, curCartItem.value.service, staleFields.value.service)
    })

    // ?? 顯示的預約類別選項 (花鳥川)
    const displayCategoryOptions = computed(() => sortOrder(resourceData.categories || []))

    // ?? 顯示的服務設備選項
    const baseResourceOptions = computed(() => {
      const selectedService = curCartItem.value.getDetailData('service')
      if (!selectedService) return [] // 尚未選擇服務項目

      let availableResources = filter(get(selectedService, 'ResourceItems'), { enabled: true, isRemove: false })

      // 如果有子項目
      if (curCartItem.value.subService) {
        const subService = curCartItem.value.subService
        const subServiceResource = get(subService, 'ResourceItems', [])
        if (!isEmpty(subServiceResource)) {
          const subServiceResourceIds = map(subServiceResource, 'id')
          availableResources = filter(availableResources, (item) => includes(subServiceResourceIds, item.id))
        }
      } else {
        if (availableResources.length) {
          availableResources.unshift({
            id: 'all',
            name: i18n.t('orderDetailBlock.resource.all.title'),
          })
        }
      }

      return sortOrder(availableResources)
    })

    // ?? 顯示的服務設備選項（補回已失效但仍被選取的歷史值）
    const displayResourceOptions = computed(() => {
      return withPreservedOption(baseResourceOptions.value, get(curCartItem.value.resources, '[0]'), staleFields.value.resources)
    })

    // ?? 顯示的服務子項目選項
    const baseSubServiceOptions = computed(() => {
      const serviceType = curCartItem.value.serviceType
      const selectedService = curCartItem.value.getDetailData('service')
      const selectedServiceUnit = curCartItem.value.serviceUnit

      if (!selectedService) return [] // 尚未選擇服務項目
      if (!selectedService.enableSubService) return [] // 未啟用子項目

      if (serviceType === serviceTypeConfig.resourceService.value) return get(selectedService, 'AppointmentSubServices')
      else if (includes(['notSpecify', 'all'], get(selectedServiceUnit, 'id'))) return get(selectedService, 'AppointmentSubServices')

      const subServices = get(selectedService, 'AppointmentSubServices')
      return filter(subServices, (item) => {
        // 子服務有綁選擇的服務人員 或 子服務沒有綁定任何服務人員(代表所有服務人員都可以選)
        return find(item.AppointmentUnits, { id: get(selectedServiceUnit, 'id') }) || item.allAppointmentUnit
      })
    })

    const displaySubServiceOptions = computed(() => {
      return withPreservedOption(baseSubServiceOptions.value, curCartItem.value.subService, staleFields.value.subService)
    })

    // ?? 顯示的附加服務項目選項
    const displayAttachServiceOptions = computed(() => {
      const base = dataPlaceholder.attachedServices || []
      // CBGISSUE-226 / CBGISSUE-243：已刪除或已從服務項目移除的附加服務屬歷史快照，
      // 保留顯示並加註原因，不可再重新選取
      const baseIds = map(base, 'id')
      const legacy = filter(curCartItem.value.attachedServices, (item) => item && !includes(baseIds, item.id))
      return [...base, ...map(legacy, (item) => markStaleOption(item, staleReasonConfig.removed))]
    })

    // ?? 各欄位的失效原因（CBGISSUE-243）
    // 「再次預約」會把舊預約的服務人員 / 服務項目 / 子項目 / 設備原封不動預帶進來，
    // 若這些設定在期間被刪除或改綁定，原本會直接變空白、下拉只剩「無資料」。
    // 這裡統一判定原因，供選項標記、欄位下方說明與送出前阻擋使用。
    const staleFields = computed(() => {
      const result = {
        serviceUnit: null,
        service: null,
        subService: null,
        resources: null,
        bookingTime: null,
        resourceBookingTime: null,
        dateTime: null,
      }
      const serviceType = curCartItem.value.serviceType

      // 服務人員：已被刪除
      // 「再次預約」預帶的是舊預約的關聯資料（含 isRemove），可選清單只會回未刪除的人員；
      // 這裡以 isRemove 判定，避免服務人員清單被權限縮限的角色被誤判成已刪除
      const serviceUnit = curCartItem.value.serviceUnit
      const serviceUnitId = get(serviceUnit, 'id')
      const hasRealServiceUnit = Boolean(serviceUnitId) && !includes(virtualUnitIds, serviceUnitId)
      if (hasRealServiceUnit && get(serviceUnit, 'isRemove')) {
        result.serviceUnit = staleReasonConfig.removed
      }

      // 服務項目：已刪除 / 服務性質被改過 / 已與所選服務人員解除綁定
      const service = curCartItem.value.service
      const serviceId = get(service, 'id')
      if (serviceId && !find(baseServiceOptions.value, { id: serviceId })) {
        if (get(service, 'isRemove')) result.service = staleReasonConfig.removed
        else if (!matchServiceType(service, serviceType)) result.service = staleReasonConfig.typeChanged
        else if (hasRealServiceUnit && !result.serviceUnit) result.service = staleReasonConfig.unbound
      }

      // 服務子項目：已刪除或已不再綁定所選服務人員
      const subService = curCartItem.value.subService
      const subServiceId = get(subService, 'id')
      if (subServiceId && !result.service && !find(baseSubServiceOptions.value, { id: subServiceId })) {
        result.subService = get(subService, 'isRemove') ? staleReasonConfig.removed : staleReasonConfig.unbound
      }

      // 服務設備：已刪除 / 已停用 / 已與服務項目解除綁定
      const resource = get(curCartItem.value.resources, '[0]')
      const resourceId = get(resource, 'id')
      if (resourceId && !includes(virtualUnitIds, resourceId) && !result.service && !find(baseResourceOptions.value, { id: resourceId })) {
        const serviceDetail = curCartItem.value.getDetailData('service')
        // 服務項目詳情的 ResourceItems 已濾掉已刪除的設備，所以找不到就是已刪除或已解除綁定
        const bound = find(get(serviceDetail, 'ResourceItems'), { id: resourceId })
        if (!bound) result.resources = staleReasonConfig.unboundFromService
        else result.resources = staleReasonConfig.disabled
      }

      // 預估服務時數 / 設備使用時數：店家調整過時間單位或服務時長，原本的時數已不在選項內
      const bookingTime = curCartItem.value.bookingTime
      if (bookingTime && !includes(baseBookingTimeOptions.value, bookingTime)) {
        result.bookingTime = staleReasonConfig.outOfRange
      }
      const resourceBookingTime = curCartItem.value.resourceBookingTime
      if (resourceBookingTime && !includes(baseResourceBookingTimeOptions.value, resourceBookingTime)) {
        result.resourceBookingTime = staleReasonConfig.outOfRange
      }

      // 預約日期 / 時段：前端已能判斷的情況（所選服務人員不在服務項目的綁定名單內）
      if (!result.service && !result.serviceUnit && hasRealServiceUnit) {
        const serviceDetail = curCartItem.value.getDetailData('service')
        const boundUnits = get(serviceDetail, 'AppointmentUnits')
        if (serviceDetail && boundUnits && boundUnits.length && !find(boundUnits, { id: serviceUnitId })) {
          result.dateTime = staleReasonConfig.unbound
        }
      }

      return result
    })

    // ?? 失效欄位的下方說明
    const staleFieldHints = computed(() => {
      const stale = staleFields.value
      const unitName = get(curCartItem.value.serviceUnit, 'name')
      const serviceName = get(curCartItem.value.service, 'name')
      return {
        serviceUnit: staleFieldHint('serviceUnit', stale.serviceUnit, { name: unitName }),
        service: staleFieldHint('service', stale.service, { name: serviceName, unitName }),
        subService: staleFieldHint('subService', stale.subService, { name: get(curCartItem.value.subService, 'name'), unitName }),
        resources: staleFieldHint('resourceItem', stale.resources, { name: get(curCartItem.value.resources, '[0].name'), serviceName }),
        bookingTime: staleFieldHint('bookingTime', stale.bookingTime, { name: curCartItem.value.bookingTime }),
        resourceBookingTime: staleFieldHint('resourceBookingTime', stale.resourceBookingTime, { name: curCartItem.value.resourceBookingTime }),
        dateTime: staleFieldHint('dateTime', stale.dateTime, { name: serviceName, unitName }),
      }
    })

    // ?? 兩個以上欄位失效時的摘要
    const staleSummary = computed(() => {
      const stale = staleFields.value
      const labels = compact([
        stale.serviceUnit && displayData.value.formItemLabel.serviceUnit,
        stale.service && displayData.value.formItemLabel.service,
        stale.subService && displayData.value.formItemLabel.subService,
        stale.resources && displayData.value.formItemLabel.resources,
      ])
      if (labels.length < 2) return ''
      return staleSummaryText(labels)
    })

    // ?? 下拉清單真的是空的時候，把「無資料」換成有原因的句子
    const staleEmptyText = computed(() => {
      const fallback = i18n.t('common.table.empty.text')
      const text = { service: fallback, subService: fallback, resources: fallback }

      if (isEmpty(baseServiceOptions.value)) {
        const serviceUnit = curCartItem.value.serviceUnit
        const serviceUnitId = get(serviceUnit, 'id')
        const categoryId = get(curCartItem.value, 'category.id')
        if (serviceUnitId && !includes(virtualUnitIds, serviceUnitId) && isEmpty(dataPlaceholder.services) && !staleFields.value.serviceUnit) {
          text.service = staleEmptyHint('service.byServiceUnit', { name: get(serviceUnit, 'name') })
        } else if (useKachogawaCategory.value && categoryId) {
          text.service = staleEmptyHint('service.byCategory', { name: get(curCartItem.value, 'category.name') })
        } else {
          text.service = staleEmptyHint('service.byServiceType')
        }
      }

      const serviceName = get(curCartItem.value.service, 'name')
      if (serviceName && isEmpty(baseSubServiceOptions.value)) {
        text.subService = staleEmptyHint('subService', { name: serviceName })
      }
      if (serviceName && isEmpty(baseResourceOptions.value)) {
        text.resources = staleEmptyHint('resourceItem', { name: serviceName })
      }

      return text
    })

    // ?? 預估服務時數選項（不含失效歷史值）
    const baseBookingTimeOptions = computed(() => {
      let nums = []
      const serviceType = curCartItem.value.serviceType
      const shopTimeUnit = get(configData, 'reservation.timeUnit')

      if (serviceType === serviceTypeConfig.humanService.value) {
        const totalTime = curCartItem.value.getAppointmentTotalTime()
        const times = generateNumbers(0, 600, shopTimeUnit)
        times.push(totalTime)
        nums = [...new Set(times.sort((a, b) => a - b))]
      } else if (serviceType === serviceTypeConfig.resourceService.value) {
        const totalTime = curCartItem.value.getAppointmentResourceTotalTime()
        const times = generateNumbers(0, 600, shopTimeUnit)
        times.push(totalTime)
        nums = [...new Set(times.sort((a, b) => a - b))]
      } else if (includes([
        serviceTypeConfig.resourceService.value,
        serviceTypeConfig.humanAndResourceService.value,
      ], serviceType)) {
        // CNCY-7418 - 2023/07/16
        const totalTime = curCartItem.value.getAppointmentResourceTotalTime()
        // const times = generateNumbers(0, totalTime + shopTimeUnit, shopTimeUnit)
        const times = generateNumbers(0, 600, shopTimeUnit)
        times.push(totalTime)
        // nums = [...new Set(filter(times.sort((a, b) => a - b), (item) => item <= totalTime))]
        nums = [...new Set(times.sort((a, b) => a - b))]
      }
      return nums
    })

    const baseResourceBookingTimeOptions = computed(() => {
      let nums = []
      const serviceType = curCartItem.value.serviceType
      const shopTimeUnit = get(configData, 'reservation.timeUnit')

      if (serviceType === serviceTypeConfig.humanService.value) {
        const totalTime = curCartItem.value.getAppointmentTotalTime()
        const times = generateNumbers(0, 600, shopTimeUnit)
        times.push(totalTime)
        nums = [...new Set(times.sort((a, b) => a - b))]
      } else if (serviceType === serviceTypeConfig.resourceService.value) {
        const totalTime = curCartItem.value.getAppointmentResourceTotalTime()
        const times = generateNumbers(0, 600, shopTimeUnit)
        times.push(totalTime)
        nums = [...new Set(times.sort((a, b) => a - b))]
      } else if (includes([
        serviceTypeConfig.resourceService.value,
        serviceTypeConfig.humanAndResourceService.value,
      ], serviceType)) {
        // CNCY-7418 - 2023/07/16
        const totalTime = curCartItem.value.getAppointmentResourceTotalTime()
        // const times = generateNumbers(0, totalTime + shopTimeUnit, shopTimeUnit)
        const times = generateNumbers(0, 600, shopTimeUnit)
        times.push(totalTime)
        // nums = [...new Set(filter(times.sort((a, b) => a - b), (item) => item <= totalTime))]
        nums = [...new Set(filter(times.sort((a, b) => a - b), (item) => item <= curCartItem.value.bookingTime))]
      }
      return nums
    })

    // ?? 顯示的預估服務時數 / 設備使用時數選項
    // CBGISSUE-243：時間單位或服務時長被調整後，原本的時數會不在選項內導致欄位變空白。
    // 這裡把原值補回選項並加註原因；時數本身仍是合法的送出值，因此保留可選取。
    const withPreservedTimeOption = (options, value) => {
      if (!value || includes(options, value)) return options
      return [...options, value].sort((a, b) => a - b)
    }
    const displayBookingTimeOptions = computed(() => {
      return withPreservedTimeOption(baseBookingTimeOptions.value, curCartItem.value.bookingTime)
    })
    const displayResourceBookingTimeOptions = computed(() => {
      return withPreservedTimeOption(baseResourceBookingTimeOptions.value, curCartItem.value.resourceBookingTime)
    })
    const timeOptionLabel = (value, field) => {
      const reason = get(staleFields.value, field)
      if (reason && value === get(curCartItem.value, field)) return staleOptionLabel(value, reason)
      return value
    }

    // ?? 顯示的加班時段選項
    const displayOvertimeTimeOptions = computed(() => {
      const offset = get(configData.reservation, 'timeUnitOffset')
      const timeUnit = get(configData.reservation, 'timeUnit')
      const selectedDate = formatDate(curCartItem.value.date, 'YYYY/MM/DD')
      const times = generateFormatPeriod({ gap: timeUnit, offset, maxEnd: 1440 })
      return map(times, (time) => {
        return {
          label: time,
          value: dayjs(`${selectedDate} ${time}`).toDate(),
        }
      })
    })

    const nowTime = dayjs()

    // 穩定 identity 的 picker-options（reactive：el-date-picker 的 deep watch 只追蹤
    // reactive 物件的內部 mutation）。僅在 dataPlaceholder 變更（availability 更新/切月查詢
    // 回傳）時重新指派 disabledDate，觸發面板重繪；非當月（前後月補位）格子一律禁用，
    // 與多時段模式（AppointmentPeriodForm）行為一致。
    const datePickerOptions = reactive({ disabledDate: () => true })
    const refreshDatePickerDisabledDate = () => {
      const year = get(datePickerRef.value, 'picker.year')
      const month = get(datePickerRef.value, 'picker.month')
      datePickerOptions.disabledDate = (date) => {
        const cur = new Date(date)

        // 非當月（前後月補位）格子禁用，避免切月/選到非當月日期
        if (cur.getFullYear() !== year) return true
        if (cur.getMonth() !== month) return true

        const isOverTime = curCartItem.value.isOverTime
        if (isOverTime) return false
        const d = dayjs(date)
        if (!find(dataPlaceholder.availableDate, (item) => item === d.format('YYYY/MM/DD'))) return true
        return d.isBefore(nowTime, 'date')
      }
    }
    refreshDatePickerDisabledDate()
    watch(dataPlaceholder, refreshDatePickerDisabledDate)

    // ?? 顯示的堂票選項
    const displayClassTicketOptions = computed(() => {
      const selectedService = curCartItem.value.getDetailData('service')
      const appointmentDateTime = curCartItem.value.time
      if (!selectedService) return []
      if (!appointmentDateTime) return []

      // 過濾已過期或無可用次數的堂票
      const availableClassTickets = filter(memberData.classTickets, (item) => {
        if (!item.availableUseTimes) return false
        if (item.isExp) {
          const expDate = item.exp
          if (!expDate) return true
          // 檢查堂票過期時間是否大於預約時段
          else if (dayjs(expDate).isAfter(dayjs(appointmentDateTime), 'date')) return true
        } else {
          return true
        }
      })

      // 過濾會員針對該服務適用的堂票
      const serviceClassTicketIds = map(selectedService.ClassTickets, 'id')
      const classTickets = map(availableClassTickets, 'ClassTicketId')
      const intersectionClassTickets = intersection(serviceClassTicketIds, uniq(classTickets))
      if (!intersectionClassTickets.length) return [] // 會員沒有可用於服務的堂票

      const finalAvailablesClassTickets = filter(availableClassTickets, (item) => includes(intersectionClassTickets, item.ClassTicketId))
      const preCartItem = []
      const curCartItemIdx = findIndex(cart.value, { id: curCartItem.value.nanoId })
      for (let i = 0; i < curCartItemIdx; i++) {
        preCartItem.push(get(cart.value, `[${i}].cartItem`))
      }

      const formatedOptions = map(finalAvailablesClassTickets, (item) => {
        // 此張堂票已經被使用的次數
        const preUseTimes = reduce(preCartItem, (sum, i) => {
          const used = find(i.classTickets, { id: item.id })
          return sum + (used ? 1 : 0)
        }, 0)

        const expDate = item.exp ? formatDate(item.exp) : ''
        let label = `${item.name} 剩餘${item.availableUseTimes - preUseTimes}張`
        if (expDate) label = `${label} （期限：${expDate}）`

        return {
          ...item,
          label,
          disabled: item.availableUseTimes - preUseTimes <= 0,
          expDate,
        }
      })

      // 排序堂票，將 disabled: true 的選項排到最後，expDate 快過期的排在前面
      formatedOptions.sort((a, b) => {
        if (a.disabled && !b.disabled) return 1
        if (!a.disabled && b.disabled) return -1
        if (a.expDate && b.expDate) {
          if (dayjs(a.expDate).isBefore(b.expDate)) return -1
          if (dayjs(a.expDate).isAfter(b.expDate)) return 1
        }
        return 0
      })

      return formatedOptions
    })
    // ?? 顯示的可用時段
    const displayAvailableTimes = computed(() => {
      const now = dayjs()
      const times = map(filter(dataPlaceholder.availableTimes, (item) => {
        return dayjs(item).isSame(curCartItem.value.date, 'date')
      }), i => {
        return {
          label: dayjs(i).format('HH:mm'),
          value: i,
        }
      })

      const availableTimes = filter(times, (item) => {
        return dayjs(item.value).isAfter(now, 'minute')
      })

      return availableTimes
    })
    // => 取得服務詳情
    const findServiceDetail = async (service) => {
      const [res, err] = await FindService({
        shopId: shopId.value,
        id: service.id,
      })
      if (err) {
        window.$message.error(err.msg)
        return null
      }
      return res
    }
    // => 取得服務人員詳情
    const findServiceUnitDetail = async (unitId) => {
      const [res, err] = await apiFormatAdaptor(FindServiceUnit)({
        shopId: shopId.value,
        id: unitId,
      })

      if (err) {
        window.$message.error(err)
        return null
      }
      return res
    }
    const resetAvailability = () => {
      cancelAvailability()
      availabilityPayload.value = null
      dataPlaceholder.availableDate = []
      dataPlaceholder.availableTimes = []
    }

    const getPickerMonthRange = () => {
      const picker = get(datePickerRef.value, 'picker')
      if (picker) return getMonth(picker.year, picker.month + 1)
      return thisMonth()
    }

    // => 取得可預約時間
    const applyAvailabilityData = async (res) => {
      if (!res || !res.times) return
      const list = []
      // 過濾已經超過當前時間的時間
      const availableTimes = filter(res.times, (item) => {
        return dayjs(item).isAfter(dayjs(), 'minute')
      })
      forEach(availableTimes, (item) => {
        list.push(dayjs(item).format('YYYY/MM/DD'))
      })
      dataPlaceholder.availableDate = Array.from(new Set(list))
      dataPlaceholder.availableTimes = res.times
      await applyPresetPeriod()
    }
    const getAvailableTimes = ({ start, end }) => {
      const selectedServiceUnitId = get(curCartItem.value.serviceUnit, 'id')
      const serviceType = curCartItem.value.serviceType
      if (serviceType === serviceTypeConfig.humanService.value) {
        if (!selectedServiceUnitId) {
          resetAvailability()
          return
        }
      }

      dataPlaceholder.availableDate = []
      dataPlaceholder.availableTimes = []
      const payload = {
        shopId: shopId.value,
        ...curCartItem.value.getAvailableTimesPayload({
          start: start.toDate(),
          end: end.toDate(),
          notSpecifyUnits: get(configData, 'reservation.NotSpecifyUnits'),
          peopleCount: context.peopleCount,
          preReservations: getCartItemPreReservationsPayload(curCartItem.value.nanoId),
        }),
      }
      availabilityPayload.value = payload
      applyAvailabilityData(getCachedData(payload))
    }

    const applyPresetPeriod = async () => {
      if (!shouldApplyPresetPeriod.value || !presetData.value.period) return

      await nextTick()
      const timeOption = find(displayAvailableTimes.value, (option) => {
        return dayjs(option.value).format('HH:mm') === presetData.value.period
      })
      shouldApplyPresetPeriod.value = false
      if (!timeOption) return

      updateCartItemData(props.cartItem.nanoId, 'cartItem.time', timeOption.value)
      await onTimeChange()
    }

    watch(availabilityQuery.data, async (res) => {
      await applyAvailabilityData(res)
    })

    watch(availabilityQuery.error, (error) => {
      if (error && !axios.isCancel(error)) window.$message.error(error.message)
    })

    // => 更新可選預約日期
    const getDateOptions = async () => {
      await nextTick()
      const loadPickerMonth = () => {
        const { start, end } = getPickerMonthRange()
        getAvailableTimes({ start: dayjs(start), end: dayjs(end) })
      }

      availabilityPickerPanelBinding.bind(datePickerRef.value, () => {
        nextTick(loadPickerMonth)
      })
      loadPickerMonth()
    }
    // => 更新預估服務時數
    const updateBookingTime = async () => {
      if (isPresetting.value) return
      await nextTick()
      if (curCartItem.value.serviceType === serviceTypeConfig.humanService.value) {
        curCartItem.value.bookingTime = curCartItem.value.getAppointmentTotalTime()
      } else if (curCartItem.value.isResourceService()) {
        curCartItem.value.bookingTime = curCartItem.value.getAppointmentTotalTime()
        curCartItem.value.resourceBookingTime = curCartItem.value.getAppointmentResourceTotalTime()
      }
    }
    // => 當服務性質變動時
    const onServiceTypeChange = async (serviceType) => {
      resetAvailability()
      if (context.appointmentMode === appointmentModeType.batchOrder) {
        pruneAfterCartItem(curCartItem.value.nanoId)
      }
      dataPlaceholder.services = []
      curCartItem.value.resetDetailData()
      curCartItem.value.resetData()
      await nextTick()
      setTimeout(() => {
        curCartItem.value.serviceType = serviceType
        if (curCartItem.value.isResourceService()) {
          dataPlaceholder.services = cloneDeep(resourceData.services)
        }

        // 為了解決 formItem 元件快速切換渲染，error msg 不會消失問題
        setTimeout(async () => {
          loading.coreFlow = true
          await clearAllFormFieldsValidate()
          await nextTick()
          loading.coreFlow = false
        }, 0)
      }, 0)
    }
    // => 當服務人員變動時
    const onServiceUnitChange = async (serviceUnit) => {
      resetAvailability()
      if (context.appointmentMode === appointmentModeType.batchOrder) {
        pruneAfterCartItem(curCartItem.value.nanoId)
      }
      dataPlaceholder.services = []
      curCartItem.value.resetDetailData()
      curCartItem.value.resetData([
        'service',
        'subService',
        'attachedServices',
        'resources',
        'date',
        'time',
        'classTickets',
        'bookingTime',
        'resourceBookingTime',
      ])
      if (!serviceUnit) return
      if (includes(['notSpecify', 'all'], serviceUnit.id)) {
        dataPlaceholder.services = cloneDeep(resourceData.services)
      } else {
        const serviceUnitData = await findServiceUnitDetail(serviceUnit.id)
        if (serviceUnitData) {
          curCartItem.value.setDetailData('serviceUnit', serviceUnitData)
          dataPlaceholder.services = sortOrder(serviceUnitData.AppointmentServices)
        }
      }
    }
    // => 當服務項目變動時
    const onServiceChange = async (service) => {
      resetAvailability()
      if (context.appointmentMode === appointmentModeType.batchOrder) {
        pruneAfterCartItem(curCartItem.value.nanoId)
      }
      curCartItem.value.resetDetailData()
      curCartItem.value.resetData([
        'attachedServices',
        'subService',
        'resources',
        'date',
        'time',
        'classTickets',
        'bookingTime',
        'resourceBookingTime',
      ])
      // formData.totalTime = totalTime.value
      // formData.resourceTotalTime = totalTime.value

      updateBookingTime()
      if (!service) return
      const serviceData = await findServiceDetail(service)
      if (serviceData) {
        curCartItem.value.setDetailData('service', serviceData)
        dataPlaceholder.attachedServices = sortOrder(serviceData.AppointmentServiceAttaches)
      }

      // presetData - date
      if (get(presetData.value, 'date') || get(presetData.value, 'period')) {
        await setPresetDateAndPeriod()
      }
    }
    // => 當預約類別變動時 (花鳥川)
    const onCategoryChange = async () => {
      resetAvailability()
      if (context.appointmentMode === appointmentModeType.batchOrder) {
        pruneAfterCartItem(curCartItem.value.nanoId)
      }
      curCartItem.value.resetDetailData(['service', 'reservation'])
      curCartItem.value.resetData([
        'service',
        'subService',
        'attachedServices',
        'resources',
        'date',
        'time',
        'classTickets',
        'bookingTime',
        'resourceBookingTime',
      ])
    }
    // => 當服務子項目變動時
    const onSubServiceChange = async (subService) => {
      resetAvailability()
      if (context.appointmentMode === appointmentModeType.batchOrder) {
        pruneAfterCartItem(curCartItem.value.nanoId)
      }
      curCartItem.value.resetData(
        compact([
          'attachedServices',
          'time',
          'date',
          'classTickets',
          isEmpty(curCartItem.value.resources) && 'resources',
        ]),
      )
      curCartItem.value.resetDetailData(['reservation'])
      updateBookingTime()
      if (get(presetData.value, 'date') || get(presetData.value, 'period')) {
        await setPresetDateAndPeriod()
      }
    }
    // => 當設備變動時
    const onResourceChange = async (resources) => {
      resetAvailability()
      if (context.appointmentMode === appointmentModeType.batchOrder) {
        pruneAfterCartItem(curCartItem.value.nanoId)
      }
      curCartItem.value.resetData(['time', 'date', 'classTickets'])
      curCartItem.value.resetDetailData(['reservation'])
      updateBookingTime()
      if (get(presetData.value, 'date') || get(presetData.value, 'period')) {
        await setPresetDateAndPeriod()
      }
    }
    // => 當附加服務變動時
    const onAttachedServicesChange = async (attachedServices) => {
      resetAvailability()
      if (context.appointmentMode === appointmentModeType.batchOrder) {
        pruneAfterCartItem(curCartItem.value.nanoId)
      }
      curCartItem.value.resetData(['time', 'date', 'classTickets'])
      curCartItem.value.resetDetailData(['reservation'])
      updateBookingTime()
      if (get(presetData.value, 'date') || get(presetData.value, 'period')) {
        await setPresetDateAndPeriod()
      }
    }
    // => 當是否為加班時段變動時
    const onIsOverTimeChange = () => {
      resetAvailability()
      if (context.appointmentMode === appointmentModeType.batchOrder) {
        pruneAfterCartItem(curCartItem.value.nanoId)
      }
      curCartItem.value.resetData(['time', 'date', 'classTickets'])
      curCartItem.value.resetDetailData(['reservation'])
    }
    // => 當日期變動時
    const onDateChange = () => {
      if (context.appointmentMode === appointmentModeType.batchOrder) {
        pruneAfterCartItem(curCartItem.value.nanoId)
      }
      curCartItem.value.resetData(['time', 'classTickets'])
      curCartItem.value.resetDetailData(['reservation'])
    }
    // => 當時間變動時
    const onTimeChange = async () => {
      const seq = beginFlow()
      if (context.appointmentMode === appointmentModeType.batchOrder) {
        pruneAfterCartItem(curCartItem.value.nanoId)
      }
      curCartItem.value.resetData(['classTickets'])
      curCartItem.value.resetDetailData(['reservation'])
      const [res, err] = await CheckReservation({
        shopId: shopId.value,
        ...curCartItem.value.getPreDetailByStartPayload({
          notSpecifyUnits: get(configData.reservation, 'NotSpecifyUnits'),
          preReservations: getCartItemPreReservationsPayload(curCartItem.value.nanoId),
          peopleCount: context.peopleCount,
        }),
      })
      if (err) {
        endFlow(seq)
        window.$message.error(err)
        return
      }
      curCartItem.value.setDetailData('reservation', res)
      setTimeout(async () => {
        await nextTick()
        await defaultSelectClassTicket({
          displayClassTicketOptions: displayClassTicketOptions.value,
          cartItem: curCartItem.value,
        })
        calculateCheckoutCashbackLimit()
      }, 0)
    }
    // => 當堂票變動時
    const onClassTicketChange = async () => {
      const seq = beginFlow()
      curCartItem.value.resetDetailData(['reservation'])
      const [res, err] = await CheckReservation({
        shopId: shopId.value,
        ...curCartItem.value.getPreDetailByStartPayload({
          notSpecifyUnits: get(configData.reservation, 'NotSpecifyUnits'),
          preReservations: getCartItemPreReservationsPayload(curCartItem.value.nanoId),
          peopleCount: context.peopleCount,
        }),
      })
      if (err) {
        endFlow(seq)
        window.$message.error(err)
        return
      }
      curCartItem.value.setDetailData('reservation', res)
      setTimeout(async () => {
        await nextTick()
        calculateCheckoutCashbackLimit()
      }, 0)
    }
    // => 當預約時間變動時
    const onBookingTimeChange = (time) => {
      resetAvailability()
      const serviceType = curCartItem.value.serviceType
      if (includes([
        serviceTypeConfig.resourceService.value,
        serviceTypeConfig.humanAndResourceService.value,
      ], serviceType)) {
        if (curCartItem.value.resourceBookingTime > time) {
          curCartItem.value.resourceBookingTime = time
        }
      }

      // reset 預約時間後面的欄位
      curCartItem.value.resetData(['date', 'time', 'classTickets'])
      curCartItem.value.resetDetailData(['reservation'])

      // if presetData - date or period
      if (get(presetData.value, 'date') || get(presetData.value, 'period')) {
        setPresetDateAndPeriod()
      }
    }
    // => 當設備預約時數變動時
    const onResourceBookingTimeChange = () => {
      resetAvailability()
      // reset 預約時間後面的欄位
      curCartItem.value.resetData(['date', 'time', 'classTickets'])
      curCartItem.value.resetDetailData(['reservation'])

      // if presetData - date or period
      if (get(presetData.value, 'date') || get(presetData.value, 'period')) {
        setPresetDateAndPeriod()
      }
    }

    // => 設定預設日期和時段
    const setPresetDateAndPeriod = async () => {
      if (presetData.value.date) {
        const start = dayjs(presetData.value.date).startOf('day')
        const end = dayjs(presetData.value.date).endOf('day')
        updateCartItemData(props.cartItem.nanoId, 'cartItem.date', presetData.value.date)
        shouldApplyPresetPeriod.value = Boolean(presetData.value.period)
        getAvailableTimes({ start, end })
        return
      }

      shouldApplyPresetPeriod.value = Boolean(presetData.value.period)
      await applyPresetPeriod()
    }

    // 監聽 context.vehicleInfoDetail.size 的變化，有變動的話執行 calculateCheckoutCashbackLimit
    watch([() => get(context, 'vehicleInfoDetail.size'), () => get(context, 'foodpandaRestaurant')], ([newSize, newRestaurant]) => {
      if (newSize || newRestaurant) calculateCheckoutCashbackLimit()
    })

    // 預帶模式旗標
    const isPresetting = ref(false)
    onMounted(async () => {
      updateCartItemData(props.cartItem.nanoId, 'formRef', formRef)

      if (!isNull(presetData.value)) {
        isPresetting.value = true
        // 預帶服務性質
        if (presetData.value.serviceType) {
          updateCartItemData(props.cartItem.nanoId, 'cartItem.serviceType', presetData.value.serviceType)
          await onServiceTypeChange(presetData.value.serviceType)
        }
        if (presetData.value.serviceUnit) {
          const serviceUnit = find(serviceUnitIOptions.value, { id: presetData.value.serviceUnit.id })
          // CBGISSUE-243：原服務人員已被刪除時保留歷史值讓欄位標記原因，而不是無聲清空；
          // 也不再去打詳情 API（會回「查無服務人員」這種店家看不懂的技術訊息）。
          // 判準用 isRemove 而不是「有沒有在可選清單裡」——可選清單（GetServiceUnit）對沒有
          // admin.appointmentUnit.allowAll 的角色會被 addQuery 縮限，用清單判斷會把這類角色
          // 自己的舊預約誤判成已刪除，導致服務項目連帶被誤標成已解除綁定而擋住送出。
          const presetServiceUnit = serviceUnit || presetData.value.serviceUnit
          updateCartItemData(props.cartItem.nanoId, 'cartItem.serviceUnit', presetServiceUnit)
          if (!get(presetData.value.serviceUnit, 'isRemove')) await onServiceUnitChange(presetServiceUnit)
        }
        // 預帶服務項目及子項目
        if (presetData.value.service) {
          updateCartItemData(props.cartItem.nanoId, 'cartItem.service', presetData.value.service)
          // CBGISSUE-243：原服務項目已被軟刪除時詳情 API 會回 404（findOne 會過濾 isRemove），
          // 這時只保留歷史值讓欄位標記「（已刪除）」並說明原因，不再打詳情 API 丟技術訊息；
          // 沒有服務項目詳情，子項目與附加服務也沒有可比對的來源，一併跳過
          const serviceRemoved = Boolean(get(presetData.value.service, 'isRemove'))
          if (!serviceRemoved) {
            await onServiceChange(presetData.value.service)
            if (presetData.value.subService) {
              updateCartItemData(props.cartItem.nanoId, 'cartItem.subService', presetData.value.subService)
              await onSubServiceChange(presetData.value.subService)
            }
            // 預帶附加服務
            if (presetData.value.attachedServices && presetData.value.attachedServices.length > 0) {
              updateCartItemData(props.cartItem.nanoId, 'cartItem.attachedServices', presetData.value.attachedServices)
              await onAttachedServicesChange(presetData.value.attachedServices)
            }
          }
        }
        // 預帶設備（純設備模式）
        if (presetData.value.resourceItem) {
          const presetResource = presetData.value.resourceItem

          // 若尚未選服務，嘗試根據設備反推一個含該設備的服務
          if (!curCartItem.value.getDetailData('service')) {
            const serviceType = curCartItem.value.serviceType

            // 從所有服務找出符合目前服務性質且包含該設備的服務
            const candidateServices = filter(resourceData.services, (s) => {
              if (serviceType === serviceTypeConfig.resourceService.value) return s.enableResourceItem && !s.enableAppointmentUnit
              if (serviceType === serviceTypeConfig.humanAndResourceService.value) return s.enableResourceItem && s.enableAppointmentUnit
              return false
            })

            for (const service of candidateServices) {
              const serviceData = await findServiceDetail(service)
              const items = get(serviceData, 'ResourceItems', [])
              const hit = find(items, { id: presetResource.id })
              if (hit) {
                updateCartItemData(props.cartItem.nanoId, 'cartItem.service', serviceData)
                await onServiceChange(serviceData)
                break
              }
            }
          }

          // 設定設備並觸發變更
          updateCartItemData(props.cartItem.nanoId, 'cartItem.resources', [presetResource])
          onResourceChange([presetResource])
          await setPresetDateAndPeriod()
        }

        // 預帶預估服務時數和預估設備使用時數
        if (presetData.value.bookingTime && presetData.value.bookingTime > 0) {
          updateCartItemData(props.cartItem.nanoId, 'cartItem.bookingTime', presetData.value.bookingTime)
          onBookingTimeChange(presetData.value.bookingTime)
        }
        if (presetData.value.resourceTotalBookingTime && presetData.value.resourceTotalBookingTime > 0) {
          updateCartItemData(props.cartItem.nanoId, 'cartItem.resourceBookingTime', presetData.value.resourceTotalBookingTime)
        }

        // 設定完預帶資料後，清除旗標
        isPresetting.value = false
      }
    })

    onBeforeUnmount(() => {
      cancelAvailability()
      availabilityPickerPanelBinding.unbind()
    })

    return {
      loading,
      formRef,
      displayData,
      showControl,
      dataPlaceholder,
      serviceTypeConfigList,
      onServiceTypeChange,
      context,
      getDateOptions,
      onServiceUnitChange,
      onSubServiceChange,
      onDateChange,
      onIsOverTimeChange,
      onAttachedServicesChange,
      onResourceChange,
      onTimeChange,
      serviceUnitIOptions,
      displayServiceUnitOptions,
      serviceUnitSelectHint,
      onServiceChange,
      onCategoryChange,
      displayCategoryOptions,
      onBookingTimeChange,
      onResourceBookingTimeChange,
      displayBookingTimeOptions,
      displayResourceBookingTimeOptions,
      displayOvertimeTimeOptions,
      displayAttachServiceOptions,
      displayServiceOptions,
      staleFields,
      staleFieldHints,
      staleSummary,
      staleEmptyText,
      staleOptionLabel,
      optionStaleReason,
      timeOptionLabel,
      displayResourceOptions,
      displaySubServiceOptions,
      datePickerOptions,
      displayClassTicketOptions,
      displayAvailableTimes,
      curCartItem,
      datePickerRef,
      onClassTicketChange,
      formRules,
      disabledControl,
      displayText,
      setPresetDateAndPeriod,
    }
  },
})
</script>

<style lang="postcss" scoped>
::v-deep(.el-form-item__label) {
  @apply flex;
}
</style>
